import { useState, type FormEvent } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useAuth, useTheme, useToast } from '../app/providers';
import { AppFooter } from '../components/AppFooter';
import { ApiError } from '../lib/api';
import { IconAlert, IconEye, IconEyeOff, IconMoon, IconSun } from '../components/Icons';
import logoFull from '../assets/logo-full.png';
import logoFullDark from '../assets/logo-full-dark.png';

export function LoginPage() {
  const { user, loading, login } = useAuth();
  const { theme, toggle: toggleTheme } = useTheme();
  const toast = useToast();
  const navigate = useNavigate();
  const location = useLocation();

  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (loading) {
    return (
      <div className="center-screen">
        <div className="stack-sm" style={{ alignItems: 'center' }}>
          <div className="skel" style={{ width: 34, height: 34, borderRadius: 10 }} />
          <span className="muted">Loading…</span>
        </div>
      </div>
    );
  }

  if (user) {
    const from = (location.state as { from?: Location } | null)?.from?.pathname ?? '/';
    return <Navigate to={from} replace />;
  }

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);

    try {
      await login(username, password);
      toast.success('Signed in.');
      navigate('/', { replace: true });
    } catch (err) {
      // The API answers 404 for Teacher and Student accounts, matching the old login page,
      // which the client restricted to admin roles. Say something useful rather than
      // leaking that distinction.
      const message =
        err instanceof ApiError && err.status === 404
          ? 'This account cannot sign in to the portal.'
          : err instanceof Error
            ? err.message
            : 'Sign in failed.';

      setError(message);
      toast.error(message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="auth">
      {/* Positioned to land exactly where the topbar's toggle sits once you are signed in,
          so the control does not jump across the screen at the moment you log in. */}
      <button
        type="button"
        className="btn btn--ghost btn--icon btn--sm auth__theme"
        onClick={toggleTheme}
        aria-label={`Switch to ${theme === 'dark' ? 'light' : 'dark'} theme`}
        title={`Switch to ${theme === 'dark' ? 'light' : 'dark'} theme`}
      >
        {theme === 'dark' ? <IconSun /> : <IconMoon />}
      </button>

      <div className="auth__form">
        <div className="auth__inner">
          {/* Same lockup, same size, same position in both themes - only the ink changes.
              The supplied artwork sets "THE" and "ACADEMY" in navy, which loses contrast on a
              near-black card, so a light-ink copy is used there. Both are rendered and CSS
              shows one, which keeps the swap instant instead of fetching on theme change. */}
          <div className="auth__brand">
            <img className="auth__logo auth__logo--light" src={logoFull} alt="ZATians' Academy" />
            <img className="auth__logo auth__logo--dark" src={logoFullDark} alt="ZATians' Academy" />
          </div>

          <h1 className="auth__title">Sign in</h1>
          <p className="auth__lede">Enter your portal credentials to continue.</p>

          <form onSubmit={onSubmit} noValidate>
            <div className="field">
              <label htmlFor="username">Username</label>
              <input
                id="username"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                autoComplete="username"
                autoFocus
                required
                aria-invalid={error ? true : undefined}
              />
            </div>

            <div className="field">
              <label htmlFor="password">Password</label>

              {/* Reveal toggle. Worth having on a shared machine at a front desk: the
                  alternative to checking what you typed is a failed attempt, and the
                  legacy app locked nobody out but did make you retype the lot. */}
              <div className="field__control">
                <input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete="current-password"
                  required
                  aria-invalid={error ? true : undefined}
                />
                <button
                  type="button"
                  className="field__reveal"
                  onClick={() => setShowPassword((shown) => !shown)}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                  aria-pressed={showPassword}
                  title={showPassword ? 'Hide password' : 'Show password'}
                  tabIndex={-1}
                >
                  {showPassword ? <IconEyeOff /> : <IconEye />}
                </button>
              </div>
            </div>

            {error && (
              <p className="field__error" role="alert" style={{ marginBottom: 'var(--sp-4)' }}>
                <IconAlert style={{ width: 14, height: 14, marginTop: 2 }} />
                <span>{error}</span>
              </p>
            )}

            <button type="submit" className="btn btn--primary btn--lg btn--block" disabled={busy}>
              {busy ? <><span className="btn__spin" />Signing in…</> : 'Sign in'}
            </button>
          </form>
        </div>
      </div>

      {/* No rail here, so the bar spans the screen as before. */}
      <AppFooter />
    </div>
  );
}
